<template>
  <header
    class="head-navbar doNotPrintMe h-100"
    :class="{
      prod: blockHeaderSafetyColor || (!environment.isDevelopment && !environment.isTestInstance),
      dev: !blockHeaderSafetyColor && environment.isDevelopment,
      staging: !blockHeaderSafetyColor && environment.isTestInstance,
    }"
  >
    <div class="head-navbar__start">
      <img
        v-if="ENV.VITE_APP_BRAND_NAME === 'EMMA'"
        class="logo hideOnMobile"
        :src="emmalogo"
        @click="goToLisaButtonTarget"
      />
      <img
        v-else-if="isLISA3"
        class="logo hideOnMobile"
        src="/lisa3.png"
        @click="goToLisaButtonTarget"
      />
      <img v-else class="logo hideOnMobile" src="/lisa.png" @click="goToLisaButtonTarget" />
      <div class="showOnMobile mobile-sidebar-toggle-container">
        <LISAButton
          class="mobile-sidebar-toggle"
          type="button"
          icon="fas fa-bars"
          rounded
          severity="secondary"
          @click="$emit('update:showSidebar', !showSidebar)"
        />
      </div>
      <span class="head-navbar__mobile-help-space" aria-hidden="true" />
      <div
        v-if="isSpecialAdmin() && !blockHeaderSafetyColor"
        class="head-navbar-dev-controls flex align-items-center gap-2"
      >
        <LISAButton
          v-if="environment.isTestInstance || environment.isDevelopment"
          severity="neutral"
          icon="fas fa-hippo"
          @click="blockHeaderSafetyColor = !blockHeaderSafetyColor"
        />
        <LISACheckbox v-if="isLISA3" v-model="isLISA3" class="m-0 p-0"> LISA 3 </LISACheckbox>
        <LISACheckbox v-if="isLISA3" v-model="isLISA3Dark" class="m-0 p-0"> Dark </LISACheckbox>
        <Checkbox v-if="!isLISA3" id="lisa3Override" v-model="isLISA3" binary></Checkbox>
        <label v-if="!isLISA3" for="lisa3Override"> LISA 3 </label>
      </div>
    </div>
    <div id="breadcrumbs" class="ellipsis breadcrumbs breadcrumb" />
    <div class="head-navbar__end">
      <div id="boardTime" class="mr-3 hideOnMobile"></div>

      <div class="flex align-items-center gap-2">
        <MeetItem :board-id="currentBoardId" />
        <LISAButton
          v-if="showNewGlobalSearch"
          v-tooltip.bottom="t('global.search')"
          type="button"
          icon="fas fa-magnifying-glass"
          rounded
          severity="secondary"
          :aria-label="t('global.search')"
          @click="$emit('openGlobalSearch')"
        ></LISAButton>
        <LISAButton
          class="head-navbar-help-button"
          icon="fas fa-question"
          type="button"
          rounded
          severity="secondary"
          @click="toggleHelpMenu"
        >
          <template v-if="isModifierSet(ModifierType.sunAndMoon) && data != null" #icon>
            <InfoTooltip>
              <template #default>
                <div class="pt-2 p-4 rounded-2xl shadow bg-white text-center w-64">
                  <div class="text-4xl mb-2">{{ data?.icon }}</div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.nightEnd") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.nightEnd) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.nauticalDawn") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.nauticalDawn) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.dawn") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.dawn) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.sunrise") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.sunrise) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.sunriseEnd") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.sunriseEnd) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.goldenHourEnd") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.goldenHourEnd) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.solarNoon") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.solarNoon) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.goldenHour") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.goldenHour) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.sunsetStart") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.sunsetStart) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.sunset") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.sunset) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.dusk") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.dusk) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.nauticalDusk") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.nauticalDusk) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.night") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.night) }}</span>
                  </div>
                  <div class="text-sm">
                    {{ t("components.HeadNavbar.sunAndMoon.nadir") }}:
                    <span class="font-bold">{{ formatTime(data?.times?.nadir) }}</span>
                  </div>
                </div>
              </template>
              <template #activator>
                {{ data?.icon }}
              </template>
            </InfoTooltip>
          </template>
        </LISAButton>
        <PrimeMenu
          ref="helpMenu"
          class="HeadNavBarScoped"
          :model="helpItems"
          :popup="true"
          style="font-size: 0.8em; width: 20em"
        />
        <Avatar
          :picture-id="pictureId"
          :avatar-size="37.71"
          data-testId="userMenuAvatar"
          :name="preferredName"
          class="hoverClick"
          @click="toggleUserMenu"
        />
        <PrimeMenu
          ref="userMenu"
          :model="userItems"
          class="HeadNavBarScoped"
          :popup="true"
          style="font-size: 0.8em; width: 20em; max-height: calc(100vh - 4rem); overflow-y: auto"
        >
          <template #item="{ item, props: itemProps }">
            <a v-bind="itemProps.action" class="flex align-items-center">
              <span v-if="item.icon" v-bind="itemProps.icon" />
              <span v-bind="itemProps.label">{{ item.label }}</span>
              <span
                v-if="item.activeLanguage"
                class="pi pi-check ml-auto text-primary"
                aria-hidden="true"
              />
            </a>
          </template>
        </PrimeMenu>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import PrimeMenu from "primevue/menu";
import { useTranslation } from "@/language/translation";
import { ref, computed } from "vue";
import type { MenuItem } from "primevue/menuitem";
import Avatar from "@/components/AvatarImage.vue";
import emmalogo from "@/assets/logos/emma.png";
import MeetItem from "@/modules/meet/MeetItem.vue";
import { languageNames, flagMap } from "../language";
import isKey from "@/functions/isKey";
import { ENV } from "@/ENV";
import LISAButton from "./shared/primevue/LISAButton.vue";
import { isDevelopment, isSpecialAdmin, isTestInstance } from "../functions/environment";
import { useSunAndMoon } from "@/functions/sunAndMoon";
import isModifierSet from "@/functions/userModifier";
import { ModifierType } from "@/functions/userModifierEnum";
import InfoTooltip from "./shared/InfoTooltip.vue";
import vTooltip from "primevue/tooltip";
import { resolveLisaButtonRoute } from "@/functions/lisaButtonNavigation";
import { LISACheckbox } from "@fi-solutions/lisa-ui";
import Checkbox from "primevue/checkbox";
import { isLISA3, isLISA3Dark } from "@/composables/useLISA3.ts";
defineProps({
  showSidebar: {
    type: Boolean,
    required: true,
  },
});
defineEmits({
  "update:showSidebar": (show: boolean) => show,
  openGlobalSearch: null,
});

function formatTime(date?: Date): string {
  return date ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--:--";
}

const helpMenu = ref<InstanceType<typeof PrimeMenu>>();
const userMenu = ref<InstanceType<typeof PrimeMenu>>();
const toggleHelpMenu = (event: any) => {
  helpMenu.value?.toggle(event);
};
const toggleUserMenu = (event: any) => {
  userMenu.value?.toggle(event);
};

const { t, locale } = useTranslation();
const router = useRouter();
const store = useStore();
const showNewGlobalSearch = computed(() => isModifierSet(ModifierType.LISA3));

const environment = computed(() => {
  return {
    isDevelopment: isDevelopment(),
    isTestInstance: isTestInstance(),
  };
});

const currentBoardId = computed(() => {
  return parseInt(router.currentRoute.value.params?.boardId as string, 10);
});
const lisaButtonTarget = computed(() => {
  return store.getters["login/lisaButtonTarget"];
});
const startUpBoardId = computed(() => {
  return store.state.login.user?.startUpBoardId as number | null | undefined;
});

const preferredName = computed(() => {
  return store.state.login.user?.preferredName;
});
const pictureId = computed(() => {
  return store.getters["login/profilePictureId"] as string | undefined;
});

const site = computed(() => {
  return store.state.login.user?.site?.name ?? "";
});
const department = computed(() => {
  return store.state.login.user?.department?.name ?? "";
});

const blockHeaderSafetyColorStorageKey = "block-header-safety-color";
const storedBlockHeaderSafetyColor = ref(
  sessionStorage.getItem(blockHeaderSafetyColorStorageKey) === "true",
);
const blockHeaderSafetyColor = computed({
  get() {
    return storedBlockHeaderSafetyColor.value;
  },
  set(v: boolean) {
    storedBlockHeaderSafetyColor.value = v;
    sessionStorage.setItem(blockHeaderSafetyColorStorageKey, String(v));
  },
});

function isDepartmentThere() {
  if (department.value) {
    return true;
  }
  return false;
}
function createLinkAndLeave(link = "") {
  if (link === "") {
    return;
  }
  const clickableLink = document.createElement("a");
  clickableLink.href = link;
  clickableLink.target = "_blank";
  clickableLink.click();
}
function openDocumentation() {
  if (document.location.hostname === "localhost") {
    createLinkAndLeave("https://wiki.service.lisapp.de/login/intern");
  } else {
    createLinkAndLeave(`https://wiki.service.lisapp.de/login/${ENV.VITE_APP_INSTANCE_KEY}`);
  }
}
function openImprint() {
  createLinkAndLeave(`https://frisch-solutions.com/${locale.value}/impressum/`);
}
function openPrivacyStatement() {
  createLinkAndLeave(`https://frisch-solutions.com/${locale.value}/datenschutz/`);
}
function goToLisaButtonTarget() {
  const target = resolveLisaButtonRoute(lisaButtonTarget.value, startUpBoardId.value);
  if (router.currentRoute.value.fullPath !== target) {
    router.push(target);
  }
}
async function changeLanguage(language: string) {
  store.dispatch("language/setLocale", language);
}
async function logout() {
  await store.dispatch("login/logout");
}
function sendEmail(address: any) {
  window.open(`mailto:${address}`, "_blank");
}
const helpItems = computed<MenuItem[]>(() => [
  {
    label: "Help-Desk",

    items: [
      {
        separator: true,
      },
      {
        label: "+49 241 51537043",
        icon: "pi pi-phone",
        command: () => {
          window.open("tel:+4924151537043", "_blank");
        },
      },
      {
        label: "support@frisch-solutions.com",
        icon: "pi pi-envelope",
        command: () => {
          sendEmail("support@frisch-solutions.com");
        },
        style: "p-menuitem-link",
      },
      {
        separator: true,
      },
      {
        label: t("router.LISAContacts"),
        visible: isModifierSet(ModifierType.LISA3),
        icon: "fas fa-address-book",
        command: () => {
          router.push({ name: "LISAContacts" });
        },
      },
      {
        label: "Changelog",
        command: () => {
          router.push("/changelog");
        },
      },
      {
        separator: true,
      },
      {
        label: t("components.HeadNavbar.documentation"),
        command: () => {
          openDocumentation();
        },
      },
      {
        label: t("components.HeadNavbar.imprint"),
        command: () => {
          openImprint();
        },
      },
      {
        label: t("components.HeadNavbar.privacyStatement"),
        command: () => {
          openPrivacyStatement();
        },
      },
    ],
  },
]);

const userItems = computed<MenuItem[]>(() => [
  {
    label: preferredName.value,
    items: [
      {
        separator: true,
      },
      {
        label: t("components.HeadNavbar.UserSettings"),
        icon: "fas fa-user-cog",
        class: "UserSettings",
        command: () =>
          router.push({
            name: "UpdateUser",
            params: { id: store.state.login.user.id.toString() },
          }),
      },
      {
        label: site.value,
        icon: "fas fa-industry",
      },
      {
        label: department.value,
        icon: "pi pi-phone",
        visible: isDepartmentThere(),
      },
      {
        separator: true,
      },
      {
        label: t("components.HeadNavbar.Language"),
        icon: "fas fa-language",
        disabled: true,
      },
      ...Object.entries(languageNames).map(([key, label]) => {
        return {
          label,
          icon: isKey(flagMap, key) ? flagMap[key] : undefined,
          activeLanguage: key.replace("_", "-") === locale.value,
          command: () => changeLanguage(key),
        };
      }),

      {
        separator: true,
      },
      {
        label: t("components.HeadNavbar.logout"),
        icon: "fas fa-sign-out",
        command: () => logout(),
      },
    ],
  },
]);

const { data } = useSunAndMoon(computed(() => isModifierSet(ModifierType.sunAndMoon)));
</script>

<style media="screen" lang="scss" scoped>
@use "../assets/css/globalVariables" as *;

.head-navbar {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 0.75rem;
  align-items: center;
  box-sizing: border-box;
  padding: 0 0.75rem;
  border: 1px solid transparent;
  border-radius: 0;
}

.head-navbar__start,
.head-navbar__end {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  min-width: 0;
}

.head-navbar__end {
  justify-content: flex-end;
}

.head-navbar__mobile-help-space {
  display: none;
}

.breadcrumbs {
  position: static;
  align-self: center;
  justify-self: center;
  min-width: 0;
  max-width: 100%;
  margin-bottom: 0 !important;
  transform: none;
}

.head-navbar-dev-controls {
  flex: none;
  white-space: nowrap;
}

@media (max-width: $breakPointXL) {
  .head-navbar {
    grid-template-columns: auto minmax(0, 1fr) auto;
  }

  .breadcrumbs {
    box-sizing: border-box;
    width: 100%;
    overflow: hidden;
    flex-wrap: nowrap;
  }

  .breadcrumbs :deep(.mobile-header-title) {
    min-width: 0;
  }

  .mobile-sidebar-toggle-container {
    display: flex !important;
    align-items: center;
  }

  .head-navbar-dev-controls {
    flex-direction: column;
    gap: 0.35rem !important;
    align-items: flex-start !important;
    font-size: 0.85rem;
  }
}

@media (max-width: $breakPointS) {
  .head-navbar {
    gap: 0.5rem;
  }

  .head-navbar__mobile-help-space {
    display: block;
    flex: 0 0 2.75rem;
    width: 2.75rem;
    height: 2.75rem;
  }

  .head-navbar-help-button {
    position: absolute;
    top: 50%;
    left: 4rem;
    z-index: 9001;
    transform: translateY(-50%);
  }
}

.dev {
  background: red !important;
  border-color: red !important;
}

.staging {
  background: cyan !important;
  border-color: cyan !important;
}

.prod {
  background: var(--p-primary-color) !important;
  border-color: var(--p-primary-color) !important;
}

.logo {
  height: 2.4em;
  cursor: pointer;
  width: 9.47em;
  background-color: var(--p-surface-0);
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
  padding: 0.125rem;
}
</style>

<style lang="scss">
.noUnderline > button {
  text-decoration: none !important;
  padding-right: 0 !important;
  padding-left: 0 !important;
}

.help-div {
  height: 36px;
  width: 36px;
}

.HeadNavBarScoped {
  .p-submenu-header {
    font-size: 1.5em !important;
  }
}
</style>
<style scoped>
.ellipsis {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  padding: 0.53rem 1rem;
  margin-bottom: 0;
  list-style: none;
  background-color: var(--p-gray-200);
  border-radius: var(--p-border-radius-md);
}
.hoverClick:hover {
  cursor: pointer;
  filter: brightness(50%);
  transition: all 0.1s ease-out;
}
</style>
